import type { FunctionComponent, PropsWithChildren } from 'react';
import styled from 'styled-components';
import { PositionAndSizeFromProps } from '../../utils/styledcomponents';
import type { PositionAndSizeProps } from '../../utils/styledcomponents';
import { getHighlightClassName } from '../../utils/animation';
import type { HighlightProps } from '../../utils/animation';
import Image from '../atoms/Image';
import { useClickCallback } from '../../utils/hooks';
import type { ImageComponent } from '../../assets/Assets';

export interface StateProps {
	soundEnabled: boolean;
	soundVolume: number;
}

export interface DispatchProps {}

interface ButtonProps extends HighlightProps {
	shadow?: boolean;
}

export type OwnProps = PropsWithChildren<
	PositionAndSizeProps &
		ButtonProps & {
			className?: string;
			src: ImageComponent;
			disabled?: boolean;
			onClick?: () => void;
		}
>;

export type Props = StateProps & DispatchProps & OwnProps;

const Button = styled.button.attrs<ButtonProps>((props) => ({
	disabled: props.disabled || !props.onClick,
	className: props.disabled ? undefined : getHighlightClassName(props)
}))<ButtonProps>`
	background: transparent;
	border: none;
	color: inherit;
	cursor: ${(props) =>
		props.onClick && !props.disabled ? 'pointer' : 'inherit'};
	font: ${(props) => props.theme.font.default};
	padding: 0;
	transition: transform 0.2s ease-out;
	&:focus {
		outline: none;
	}
	&:enabled:hover {
		transition: transform 0.2s ease-in;
		transform: scale(1.05);
	}
	& > svg {
		transition: filter 0.25s;
		filter: ${(props) =>
			props.shadow
				? `drop-shadow(${props.theme.shadow.imageButtonBorder}) drop-shadow(${props.theme.shadow.imageButton})`
				: undefined};
	}
	&:enabled:active > svg {
		transition: filter 0.25s;
		filter: ${(props) =>
			props.shadow
				? `drop-shadow(${props.theme.shadow.imageButtonBorder})`
				: undefined};
	}
`;

const ImageButton: FunctionComponent<Props> = (
	props: PropsWithChildren<Props>
) => {
	const {
		className,
		onClick,
		src,
		soundEnabled,
		soundVolume,
		children,
		...otherProps
	} = props;
	const onClickThrottleWithSound = useClickCallback(
		onClick,
		soundEnabled,
		soundVolume
	);

	return (
		<Button
			className={className}
			onClick={onClickThrottleWithSound}
			{...otherProps}
		>
			<Image src={src} width="100%" height="100%" />
			{children}
		</Button>
	);
};

export default styled(ImageButton)<PositionAndSizeProps>(
	PositionAndSizeFromProps
);
